'use client'

import { AnimatePresence, m, useReducedMotion } from 'motion/react'
import { useId, useState } from 'react'

/**
 * Editorial accordion. Hairline rules between items, serif questions,
 * a typographic "+ / —" marker on the right. No vermilion in chrome.
 */
export function FaqAccordion({
    items,
}: {
    items: ReadonlyArray<{ q: string; a: string }>
}) {
    const [open, setOpen] = useState<number | null>(0)
    const reduce = useReducedMotion()
    const baseId = useId()

    return (
        <ul className="border-y border-ink/15">
            {items.map((item, i) => {
                const isOpen = open === i
                const buttonId = `${baseId}-button-${i}`
                const panelId = `${baseId}-panel-${i}`
                const num = String(i + 1).padStart(2, '0')

                return (
                    <li
                        key={item.q}
                        className={
                            i < items.length - 1 ? 'border-b border-ink/15' : ''
                        }
                    >
                        <h3 className="m-0">
                            <button
                                id={buttonId}
                                type="button"
                                aria-expanded={isOpen}
                                aria-controls={panelId}
                                onClick={() => setOpen(isOpen ? null : i)}
                                className="grid w-full grid-cols-[auto_1fr_auto] items-baseline gap-5 px-4 py-6 text-left text-ink transition-colors hover:bg-paper-warm/40 focus-visible:ring-2 focus-visible:ring-vermilion focus-visible:ring-offset-2 focus-visible:outline-none sm:px-6"
                            >
                                <span className="font-serif text-[11px] tracking-[0.28em] text-muted-fg uppercase">
                                    {num}
                                </span>
                                <span className="font-serif text-[19px] leading-snug font-normal md:text-[22px]">
                                    {item.q}
                                </span>
                                <span
                                    aria-hidden
                                    className="relative inline-block font-serif text-[18px] leading-none text-muted-fg tabular-nums"
                                >
                                    {/* invisible glyph sets the slot's intrinsic size + baseline */}
                                    <span className="invisible">+</span>
                                    <span
                                        className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ease-out motion-reduce:transition-none ${
                                            isOpen
                                                ? '-rotate-90 opacity-0'
                                                : 'rotate-0 opacity-100'
                                        }`}
                                    >
                                        +
                                    </span>
                                    <span
                                        className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ease-out motion-reduce:transition-none ${
                                            isOpen
                                                ? 'rotate-0 opacity-100'
                                                : 'rotate-90 opacity-0'
                                        }`}
                                    >
                                        −
                                    </span>
                                </span>
                            </button>
                        </h3>
                        {reduce ? (
                            isOpen && (
                                <div
                                    id={panelId}
                                    role="region"
                                    aria-labelledby={buttonId}
                                    className="px-4 pt-2 pb-7 text-[15px] leading-[1.7] text-body-fg sm:pr-12 sm:pl-16"
                                >
                                    {item.a}
                                </div>
                            )
                        ) : (
                            <AnimatePresence initial={false}>
                                {isOpen && (
                                    <m.div
                                        id={panelId}
                                        role="region"
                                        aria-labelledby={buttonId}
                                        initial={{ height: 0 }}
                                        animate={{ height: 'auto' }}
                                        exit={{ height: 0 }}
                                        transition={{
                                            duration: 0.35,
                                            ease: [0.32, 0.72, 0, 1],
                                        }}
                                        className="overflow-hidden"
                                    >
                                        <p className="px-4 pt-2 pb-7 text-[15px] leading-[1.75] text-body-fg sm:pr-12 sm:pl-16">
                                            {item.a}
                                        </p>
                                    </m.div>
                                )}
                            </AnimatePresence>
                        )}
                    </li>
                )
            })}
        </ul>
    )
}
